/* AdminTicker — Bloomberg-style live activity ticker for /admin.
 *
 * iter100.240 · Streams the same 5-source activity feed used by the
 * Recent Activity section at the bottom of the Overview page, but as a
 * horizontally-marqueed ribbon so admins get a passive live pulse of
 * "the platform is doing things right now" without having to scroll.
 *
 * Data source: SSE stream at GET /api/admin/activity/stream?token=X
 *   • Initial: current feed pushed as one `update` event.
 *   • Live: server re-checks every 15s; pushes a fresh `update` only
 *     when the feed signature changes. Heartbeat colon-comments between.
 *
 * UX:
 *   • Compact 36px-tall strip. Green pulse dot on the left = LIVE.
 *   • Items scroll right-to-left via a CSS keyframe animation.
 *   • `animation-play-state: paused` on hover so admin can read.
 *   • Every item is a <Link>; click drills into the source record.
 *   • Empty state: friendly "Quiet moment" line, no marquee.
 *   • Reconnect: browser EventSource auto-reconnects with exponential
 *     backoff — no custom retry logic needed.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
    ChatCircleDots, EnvelopeSimple, UserPlus, CalendarBlank, Briefcase,
} from "@phosphor-icons/react";
import useAdminActivityStream from "@/hooks/useAdminActivityStream";

// Icon + colour per source. Matches the activity feed's visual grammar
// used at the bottom of the Overview page for a coherent look.
const SOURCE_STYLE = {
    lead:             { Icon: ChatCircleDots,  tone: "text-rose-600",    bg: "bg-rose-50",    label: "Lead" },
    message:          { Icon: EnvelopeSimple,  tone: "text-indigo-600",  bg: "bg-indigo-50",  label: "Msg" },
    application:      { Icon: Briefcase,       tone: "text-amber-700",   bg: "bg-amber-50",   label: "Apply" },
    appointment:      { Icon: CalendarBlank,   tone: "text-emerald-700", bg: "bg-emerald-50", label: "Appt" },
    "patient-signup": { Icon: UserPlus,        tone: "text-teal-700",    bg: "bg-teal-50",    label: "Patient" },
};

// Every item type deep-links to the right admin surface.
// iter100.243 — Prefer per-record admin detail URLs when the item type
// has one. Falls back to the category list for events that don't have
// a dedicated per-record admin page (messages, appointments).
function deepLinkFor(item) {
    const id = item.id;
    switch (item.source) {
        case "lead":            return id ? `/admin/leads/web/${id}`         : "/admin/leads?tab=web-leads";
        case "application":     return id ? `/admin/leads/application/${id}` : "/admin/leads?tab=application";
        case "patient-signup":  return id ? `/admin/patients/${id}`          : "/admin/patients";
        case "message":         return "/admin/messages";
        case "appointment":     return "/admin/appointments";
        default:                return "/admin";
    }
}

function relativeTime(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "";
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}


export default function AdminTicker() {
    /* iter100.247 — SSE handling extracted into the shared
       useAdminActivityStream hook so both the ticker and the Recent
       Activity section at the bottom of Overview stay perfectly in
       sync with the same live feed. */
    const { items, connected } = useAdminActivityStream(15);

    // Loading state: skeleton strip
    if (items === null) {
        return (
            <div
                data-testid="admin-ticker"
                data-state="loading"
                className="h-10 rounded-full bg-secondary/50 border border-border animate-pulse"
            />
        );
    }

    // Empty state: quiet moment
    if (items.length === 0) {
        return (
            <div
                data-testid="admin-ticker"
                data-state="empty"
                className="h-10 rounded-full bg-secondary/40 border border-border px-4 flex items-center justify-between text-[12px] text-muted-foreground"
            >
                <div className="flex items-center gap-2">
                    <PulseDot connected={connected} tone="neutral" />
                    <span className="uppercase text-[10px] font-bold tracking-[0.14em]">Live</span>
                </div>
                <span className="italic">Quiet moment — no activity in the last hour.</span>
                <span />
            </div>
        );
    }

    // Loop the items to make the marquee seamless.
    const looped = [...items, ...items];

    return (
        <div
            data-testid="admin-ticker"
            data-state="live"
            className="h-10 rounded-full bg-gradient-to-r from-secondary/60 via-card to-secondary/60 border border-border flex items-center overflow-hidden shadow-sm"
        >
            {/* LEFT · Live indicator */}
            <div className="flex items-center gap-2 pl-4 pr-3 border-r border-border h-full flex-shrink-0 bg-card/60">
                <PulseDot connected={connected} tone="live" />
                <span className="uppercase text-[10px] font-bold tracking-[0.14em] text-emerald-700">
                    Live
                </span>
            </div>

            {/* RIGHT · Marquee of items */}
            <div className="flex-1 overflow-hidden relative group">
                <div
                    className="flex items-center gap-6 whitespace-nowrap animate-ticker group-hover:[animation-play-state:paused]"
                    style={{
                        // The keyframe animates translateX from 0 to -50%
                        // (because we render items twice — see `looped`).
                        // Duration scales with item count so a busy day
                        // still reads at a comfortable pace.
                        // iter100.245 — Second speed bump per user
                        // request. Was Math.max(12, items.length * 2.5)
                        // → ~37s loop on 15 items. Now Math.max(8,
                        // items.length * 1.5) → ~22s. Still readable
                        // at a glance; hover-pause still available for
                        // slower reads.
                        animationDuration: `${Math.max(8, items.length * 1.5)}s`,
                    }}
                    data-testid="admin-ticker-track"
                >
                    {looped.map((item, idx) => {
                        const style = SOURCE_STYLE[item.source] || {
                            Icon: ChatCircleDots, tone: "text-muted-foreground", bg: "bg-secondary", label: "Event",
                        };
                        const { Icon } = style;
                        return (
                            <Link
                                key={`${item.source}-${item.id}-${idx}`}
                                to={deepLinkFor(item)}
                                data-testid={`ticker-item-${item.source}`}
                                className="inline-flex items-center gap-2 text-[12px] hover:opacity-80 transition-opacity"
                            >
                                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${style.bg}`}>
                                    <Icon size={11} weight="bold" className={style.tone} />
                                </span>
                                <span className={`text-[9.5px] font-bold uppercase tracking-[0.12em] ${style.tone}`}>
                                    {style.label}
                                </span>
                                <span className="text-foreground font-medium truncate max-w-[280px]">
                                    {item.title}
                                </span>
                                <span className="text-muted-foreground/80 text-[11px]">
                                    · {relativeTime(item.created_at)}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}


/**
 * Green pulse dot when live-connected, grey/amber when disconnected.
 * Uses a CSS-only animation (radial-gradient + ping keyframe).
 */
function PulseDot({ connected, tone = "live" }) {
    if (tone === "neutral") {
        return <span className="w-2 h-2 rounded-full bg-muted-foreground/50" />;
    }
    if (!connected) {
        return (
            <span className="relative inline-flex">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
            </span>
        );
    }
    return (
        <span className="relative inline-flex">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
        </span>
    );
}
